<script setup>
/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearning} AuditLearning */

import { storeToRefs } from 'pinia';
import { i18n } from 'src/boot/i18n';
import Breadcrumbs from 'src/components/Breadcrumbs/Breadcrumbs.vue';
import RecordsTable from 'src/components/UI/RecordsTable.vue';
import {
  confirmDestructiveDialog,
  HrAdaptiveMenu,
  HrDivider,
  HrIconMenuBtn,
  HrMenuItem,
  HrPage,
  HrTd,
  HrTooltip,
  notifyNegative,
  notifySuccess,
  openComponentDialog,
} from 'src/design-system';
import { auth as useAuthStore } from 'src/store/auth';
import { computed, onMounted, ref } from 'vue';

import { useAuditLearnings } from '../composables/useAuditLearnings';
import { useLearningsAnalytics } from '../composables/useLearningsAnalytics';
import { presentAuditLearningRow } from '../presenters/auditLearningRowPresenter';
import EditLearningDialog from './EditLearningDialog.vue';

defineOptions({ name: 'LearningsModeration' });

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const openRowMenuId = ref(null);

const pagination = ref({
  sortBy: 'learning',
  descending: false,
  page: 1,
  rowsPerPage: 50,
});

/*
 * No column declares `filterable`, which is what removes the Add filter control: the table derives it
 * from the columns rather than from a prop.
 */
const columns = computed(() => [
  {
    name: 'learning',
    label: i18n.t('noteAuditor.learningsModeration.columns.learning'),
    align: 'left',
    field: 'learning',
    sortable: false,
  },
  {
    name: 'statusLabel',
    label: i18n.t('noteAuditor.learningsModeration.columns.status'),
    align: 'left',
    field: 'statusLabel',
    sortable: false,
  },
  {
    name: 'sourceLabel',
    label: i18n.t('noteAuditor.learningsModeration.columns.source'),
    align: 'left',
    field: 'sourceLabel',
    sortable: false,
  },
  {
    name: 'appliesToLabel',
    label: i18n.t('noteAuditor.learningsModeration.columns.appliesTo'),
    align: 'left',
    field: 'appliesToLabel',
    sortable: false,
  },
]);

/** @type {import('vue').Ref<AuditLearning[]>} */
const learnings = ref([]);
const sessionNoteTemplates = ref([]);

const {
  isProcessing,
  loadLearnings,
  loadSessionNoteTemplates,
  removeLearning,
} = useAuditLearnings();

const {
  LEARNING_STATUS_CHANGE_CAUSES,
  LEARNING_STATUSES: ANALYTICS_STATUSES,
  describeDeleteVariant,
  describeScope,
  draftAgeInMinutes,
  trackEditStarted,
  trackLearningDeleted,
  trackStatusChanged,
} = useLearningsAnalytics();

const clinicId = computed(() => userDetails.value?.clinic ?? null);

const learningsById = computed(() => learnings.value.reduce((acc, learning) => {
  acc[learning.id] = learning;
  return acc;
}, {}));

const learningsRows = computed(() => learnings.value.map(presentAuditLearningRow));

/**
 * Load all learnings for current clinic.
 * @returns {Promise<void>}
 */
async function fetchLearnings() {
  if (!clinicId.value) {
    return;
  }

  try {
    learnings.value = await loadLearnings(clinicId.value);
  } catch (err) {
    console.error('Failed to load audit learnings', err);
    notifyNegative(i18n.t('common.errors.generic'));
  }
} // fetchLearnings()

/**
 * @returns {Promise<void>}
 */
async function fetchSessionNoteTemplates() {
  if (!clinicId.value) {
    return;
  }

  try {
    sessionNoteTemplates.value = await loadSessionNoteTemplates(clinicId.value);
  } catch (err) {
    console.error('Failed to load session note templates', err);
    notifyNegative(i18n.t('common.errors.generic'));
  }
} // fetchSessionNoteTemplates()

/**
 * A save can create a second row, delete one, or swap two of them, so the list is re-read rather than
 * patched. It is one query over a small collection, and it keeps the parent/draft pairing on screen
 * consistent with what was actually written.
 *
 * @param {{ created: boolean, promoted: boolean, savedAsDraft: boolean }} outcome
 * @returns {Promise<void>}
 */
async function onLearningSaved({ created, promoted, savedAsDraft }) {
  await fetchLearnings();

  // A draft is worth saying so about whether it is new or not: what matters to the reader is that it is
  // not applied to audits yet.
  if (savedAsDraft) {
    notifySuccess(i18n.t('noteAuditor.learningsModeration.notifications.learningSavedAsDraft'));
    return;
  }

  if (created) {
    notifySuccess(i18n.t('noteAuditor.learningsModeration.notifications.learningCreated'));
    return;
  }

  notifySuccess(i18n.t(promoted
    ? 'noteAuditor.learningsModeration.notifications.learningPromoted'
    : 'noteAuditor.learningsModeration.notifications.learningUpdated'));
} // onLearningSaved()

/**
 * @param {AuditLearning|null} learningRecord
 */
function openLearningDialog(learningRecord) {
  const { learningEditAttemptId } = trackEditStarted({
    learning: learningRecord,
    totalLearnings: learnings.value.length,
  });

  openComponentDialog(
    EditLearningDialog,
    {
      clinicId: clinicId.value,
      learningRecord,
      parentLearning: learningRecord?.parentId
        ? (learningsById.value[learningRecord.parentId] ?? null)
        : null,
      sessionNoteTemplates: sessionNoteTemplates.value,
      learningEditAttemptId,
    },
    onLearningSaved,
    // Cancelling while a save is in flight abandons the result, not the write it may already have made,
    // so the list is re-read rather than left showing a state that is no longer true.
    fetchLearnings,
  );
} // openLearningDialog()

/**
 * @returns {void}
 */
function onCreateLearning() {
  if (!clinicId.value) {
    notifyNegative(i18n.t('common.errors.generic'));
    return;
  }

  openLearningDialog(null);
} // onCreateLearning()

const createAction = computed(() => ({
  key: 'create-learning',
  label: i18n.t('noteAuditor.learningsModeration.buttonCreateLearning'),
  disable: isProcessing.value,
  dataE2e: 'button-create-learning',
  handler: onCreateLearning,
}));

/**
 * Open dialog for editing a learning.
 *
 * An active learning with a revision already open routes to that revision, so a second one is never
 * started for the same learning.
 *
 * @param {{ editTargetId: string }} row
 * @return {void}
 */
function onEditLearning(row) {
  if (!clinicId.value) {
    notifyNegative(i18n.t('common.errors.generic'));
    return;
  }

  const learningRecord = learningsById.value[row.editTargetId];

  if (!learningRecord) {
    notifyNegative(i18n.t('common.errors.generic'));
    return;
  }

  openLearningDialog(learningRecord);
} // onEditLearning()

/**
 * Ask confirmation and delete a learning if confirmed.
 *
 * Deleting an active learning that has a revision open keeps that revision as a standalone draft, so the
 * dialog says so rather than letting the manager discover it in the table afterwards.
 *
 * @param {{ id: string, hasOpenDraftRevision: boolean }} row
 * @returns {void}
 */
function onDeleteLearning(row) {
  if (!clinicId.value) {
    notifyNegative(i18n.t('common.errors.generic'));
    return;
  }

  const message = [
    i18n.t('noteAuditor.learningsModeration.dialogs.deleteLearningMessage'),
    row.hasOpenDraftRevision
      ? i18n.t('noteAuditor.learningsModeration.dialogs.deleteLearningDraftNotice')
      : null,
  ].filter(Boolean).join(' ');

  const learningRecord = learningsById.value[row.id] ?? null;

  confirmDestructiveDialog(
    i18n.t('noteAuditor.learningsModeration.dialogs.deleteLearningTitle'),
    message,
    async () => {
      try {
        await removeLearning(clinicId.value, row.id);

        if (learningRecord) {
          trackLearningDeleted({
            learningId: learningRecord.id,
            variant: describeDeleteVariant(learningRecord),
            source: learningRecord.source ?? null,
            scope: describeScope(learningRecord.sessionNoteTemplateIds),
            templateCount: learningRecord.sessionNoteTemplateIds?.length ?? 0,
          });

          const orphanedDraft = learningRecord.draftRevisionId
            ? learningsById.value[learningRecord.draftRevisionId]
            : null;

          if (orphanedDraft) {
            trackStatusChanged({
              learningId: orphanedDraft.id,
              fromStatus: ANALYTICS_STATUSES.REVISION_DRAFT,
              toStatus: ANALYTICS_STATUSES.DRAFT,
              cause: LEARNING_STATUS_CHANGE_CAUSES.PARENT_DELETED,
              draftAgeMinutes: draftAgeInMinutes(orphanedDraft),
            });
          }
        }

        await fetchLearnings();
        notifySuccess(i18n.t('noteAuditor.learningsModeration.notifications.learningDeleted'));
      } catch (err) {
        console.error('Failed to delete audit learning', err);
        notifyNegative(i18n.t('common.errors.generic'));
      }
    },
    { confirmLabel: i18n.t('common.buttonDelete') },
  );
} // onDeleteLearning()

onMounted(() => {
  fetchLearnings();
  fetchSessionNoteTemplates();
});
</script>

<template>
  <hr-page>
    <breadcrumbs :custom-page-label="$t('settings.learningsModeration.breadcrumbElLabel')" />

    <div class="q-pa-none">
      <records-table
        v-model:pagination="pagination"
        :title="i18n.t('settings.learningsModeration.breadcrumbElLabel')"
        :search-placeholder="i18n.t('noteAuditor.learningsModeration.inputSearch')"
        :rows="learningsRows"
        :columns="columns"
        :loading="isProcessing"
        :default-columns="['learning', 'statusLabel', 'sourceLabel', 'appliesToLabel']"
        :description="i18n.t('noteAuditor.learningsModeration.caption')"
        :create="createAction"
        disable-export
        :no-data-label="
          learningsRows?.length > 0
            ? i18n.t('noteAuditor.learningsModeration.tableNoResultsLabel')
            : i18n.t('noteAuditor.learningsModeration.tableNoDataLabel')
        "
        row-key="id"
        standalone
      >
        <template #body-cell-learning="props">
          <hr-td :props="props">
            <div class="text-pre-wrap">
              {{ props.row.learning }}
            </div>
          </hr-td>
        </template>

        <template #body-cell-appliesToLabel="props">
          <hr-td :props="props">
            {{ props.row.appliesToLabel }}
            <hr-tooltip v-if="props.row.appliesToTooltip">
              <div class="text-pre-wrap">
                {{ props.row.appliesToTooltip }}
              </div>
            </hr-tooltip>
          </hr-td>
        </template>

        <template #row-actions="{ row }">
          <hr-icon-menu-btn
            size="sm"
            :aria-label="i18n.t('noteAuditor.learningsModeration.labelRowActions')"
            :disable="isProcessing"
            data-e2e="button-learning-actions"
            @click.stop="openRowMenuId = openRowMenuId === row.id ? null : row.id"
          >
            <hr-adaptive-menu
              :model-value="openRowMenuId === row.id"
              @update:model-value="openRowMenuId = $event ? row.id : null"
            >
              <hr-menu-item
                v-close-popup
                icon="edit"
                :label="i18n.t('common.buttonEdit')"
                data-e2e="button-edit-learning"
                @click="onEditLearning(row)"
              />
              <hr-divider />
              <hr-menu-item
                v-close-popup
                icon="delete"
                destructive
                :label="i18n.t('common.buttonDelete')"
                data-e2e="button-delete-learning"
                @click="onDeleteLearning(row)"
              />
            </hr-adaptive-menu>
          </hr-icon-menu-btn>
        </template>
      </records-table>
    </div>
  </hr-page>
</template>

<style scoped>
.text-pre-wrap {
  white-space: pre-wrap;
}
</style>
